import React, { useState, useRef, useEffect } from 'react';
import { Flex } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import heroImage from '../assets/1.jpg';
import chatImage from '../assets/chat.png';
import exportImage from '../assets/export.png';
import aiAssistantImage from '../assets/ai_assistant.png';
import proseImage from '../assets/prose.png';
import statsImage from '../assets/stats.png';
import logoImage from '../assets/logo.png';
import priceImage from '../assets/price.jpg';
import worldImage from '../assets/world.png';
import apiKeyImage from '../assets/openrouter.png';
import LoginModal from '../components/LoginModal';
import api from '../services/api';
import { useStore } from '../store/store';
import PurpleMatrix from './PurpleMatrix';

// Marketing blurbs data
const marketingBlurbs = [
  {
    headline: "Your Novel, Your Way – AI-Powered or Fully Yours",
    description: "Generate an entire novel with AI—from logline to final prose—or take full creative control. Mix AI assistance with your own writing style for the perfect balance of automation and artistry.",
    image: proseImage
  },
  {
    headline: "Rewrite with Precision – AI That Understands Your Vision",
    description: "Select any part of your prose and ask AI to rewrite it with your specific instructions in mind. Fine-tune every sentence until it's exactly how you want it.",
    image: aiAssistantImage
  },
  {
    headline: "Your Personal AI Writing Coach",
    description: "Use AI chat to brainstorm plot twists, analyze a single scene, or get insights into your entire novel. Whether you need feedback or fresh ideas, AI is always ready to assist.",
    image: chatImage
  },
  {
    headline: "A Smarter Way to Build Worlds",
    description: "Characters, locations, and lore—organized in one place and seamlessly woven into your story by AI. Keep your world consistent, immersive, and uniquely yours.",
    image: worldImage
  },
  {
    headline: "See Your Story Like Never Before",
    description: "Track word count, dialogue balance, POV distribution, and AI generation costs with powerful analytics. Stay in control of your novel's structure and pacing.",
    image: statsImage
  },
  {
    headline: "Seamless Export – Your Words, Ready to Publish",
    description: "Export your novel in DOCX, PDF, or Markdown. Whether you're polishing your manuscript or preparing for publication, your work is always ready to go.",
    image: exportImage
  },
  {
    headline: "One Price. Every Feature. No Limits.",
    description: "No confusing tiers, no locked features—just everything you need to write your novel, all for one simple price.",
    image: priceImage
  },
  {
    headline: "Your AI, Your Terms – Bring Your Own Key",
    description: "No hidden AI fees—connect your OpenRouter key and use AI as much or as little as you want. Full flexibility, full transparency.",
    image: apiKeyImage
  }
];

export const LandingPage = () => {
    const [tilt, setTilt] = useState({ x: 5.5, y: -5.5 });
    const [isHovering, setIsHovering] = useState(false);
    const imageRef = useRef(null);
    // For sparkle effect
    const [sparkles, setSparkles] = useState([]);
    // For login modal
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const navigate = useNavigate();
    const [state, actions] = useStore();
    
    // Generate random sparkle
    const generateSparkle = () => ({
        id: Math.random(),
        createdAt: Date.now(),
        size: Math.random() * 10 + 5,
        style: {
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
            opacity: Math.random() * 0.8 + 0.2,
        },
    });

    // Add sparkle effect
    useEffect(() => {
        const interval = setInterval(() => {
            setSparkles(sparkles => [
                ...sparkles.filter(sparkle => Date.now() - sparkle.createdAt < 700),
                generateSparkle()
            ]);
        }, 150);
        
        return () => clearInterval(interval);
    }, []);

    // Check if user is already logged in on page load
    useEffect(() => {
        const loggedInUser = localStorage.getItem('user');
        if (loggedInUser) {
            // User is already logged in, redirect to dashboard
            navigate('/dashboard');
        }
    }, [navigate]);

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseMove = (e) => {
        if (!imageRef.current) return;
        
        const rect = imageRef.current.getBoundingClientRect();
        
        // Calculate mouse position relative to the center of the element
        const centerX = rect.left + rect.width / 10;
        const centerY = rect.top + rect.height / 10;
        
        // Calculate distance from center (normalized between -1 and 1)
        let x = (e.clientX - centerX) / (rect.width / 10);
        let y = (e.clientY - centerY) / (rect.height / 10);
        
        // Reduced multiplier for subtler effect
        setTilt({ x: y * 1.5, y: -x * 1.5 });
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
        setTilt({ x: 5.5, y: -5.5 });
    };

    const handleLoginClick = () => {
        setIsLoginModalOpen(true);
    };

    const handleCloseLoginModal = () => {
        setIsLoginModalOpen(false);
    };
    
    // Handle successful login
    const handleLoginSuccess = async (user) => {
        console.log('Login successful in LandingPage component');
        
        // If the user doesn't have subscription fields, try to fetch the full data
        if (user.trial_end_date === undefined || user.subscription_active === undefined) {
            console.log('Fetching complete user data after login...');
            try {
                // Fetch fresh user data from the backend
                const response = await api.getUserData(user.id);
                if (response.success) {
                    // Update localStorage with complete data
                    localStorage.setItem('user', JSON.stringify(response.user));
                    actions.setUser(response.user);
                    navigate('/dashboard');
                    return;
                }
            } catch (err) {
                console.error('Error fetching complete user data:', err);
            }
        }
        
        // Continue with normal flow if the additional fetch wasn't needed or failed
        navigate('/dashboard');
    };

    const tiltStyle = {
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale3d(${isHovering ? 1.03 : 1}, ${isHovering ? 1.03 : 1}, 1)`,
        transition: isHovering ? 'transform 0.2s ease-out' : 'transform 0.5s ease',
    };

    // Create refs for blurb images for tilt effect
    const blurbImageRefs = useRef([]);
    
    // Set up the image refs with initial values
    useEffect(() => {
        blurbImageRefs.current = blurbImageRefs.current.slice(0, marketingBlurbs.length);
    }, []);
    
    // Handle the subtle tilt effect on blurb images
    const handleBlurbImageMouseMove = (e, index) => {
        const imgElement = blurbImageRefs.current[index];
        if (!imgElement) return;
        
        const rect = imgElement.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Calculate distance from center (normalized)
        const x = ((e.clientX - centerX) / (rect.width / 2)) * 3; // 3 degrees max rotation
        const y = ((e.clientY - centerY) / (rect.height / 2)) * 3;
        
        imgElement.style.transform = `perspective(1000px) rotateY(${-x}deg) rotateX(${y}deg)`;
    };
    
    // Reset tilt on mouse leave
    const handleBlurbImageMouseLeave = (index) => {
        const imgElement = blurbImageRefs.current[index];
        if (!imgElement) return;
        imgElement.style.transform = 'perspective(1000px) rotateY(-5deg)';
    };

    return (
        <div className="landing-container">
            {/* Add the gradient background container */}
            <div className="landing-background">
                <div className="gradient-circle"></div>
                <div className="gradient-overlay"></div>
                <PurpleMatrix />
            </div>
            
            <div className="landing-header">
                <div className="header-container">
                    <div className="header-logo">
                        <img src={logoImage} alt="PlotGoblin Logo" />
                        <span className="logo-text">PlotGoblin</span>
                    </div>
                    <div className="header-auth">
                        <button className="login-btn" onClick={handleLoginClick}>Log In</button>
                        <button className="signup-btn" onClick={handleLoginClick}>Try For Free</button>
                    </div>
                </div>
            </div>
            <Flex className="hero-container" align="center">
                <div style={{ width: '100%'}}>
                <div className="hero-text">
                    Unleash Your Story.<br /><span style={{fontSize: '50px'}}>Let <span className="ai-text-wrapper">
                        <span className="ai-purple-gradient">AI</span>
                        {sparkles.map(sparkle => (
                            <span
                                key={sparkle.id}
                                className="sparkle"
                                style={{
                                    ...sparkle.style,
                                    fontSize: `${sparkle.size}px`,
                                }}
                            >
                                ✦
                            </span>
                        ))}
                    </span> Do the Heavy Lifting.</span>
                </div>
                    <div className="hero-cta-container">
                        <button className="hero-cta-button" onClick={handleLoginClick}>
                            Start Your Free Trial
                            <span className="hero-cta-arrow">→</span>
                        </button>
                    </div>
                </div>
                <div 
                    className='hero-image tilt-effect'
                    ref={imageRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    style={{
                        ...tiltStyle,
                        backgroundImage: `url(${heroImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: '0 0',
                        backgroundRepeat: 'no-repeat',
                        height: '100vh',
                        width: '150%'
                    }}
                >
                </div>
            </Flex>

            <div className="below-fold">
                <div className="below-fold-container">
                    <h2 className="below-fold-title">Write Better Novels, Faster</h2>
                    
                    <div className="blurbs-grid">
                        {marketingBlurbs.map((blurb, index) => (
                            <React.Fragment key={index}>
                                <div 
                                    className={`blurb-item ${index % 2 === 0 ? 'blurb-item-left' : 'blurb-item-right'}`}
                                >
                                    <div className="blurb-image-container">
                                        <img 
                                            src={blurb.image} 
                                            alt={blurb.headline}
                                            className="blurb-image"
                                            ref={el => blurbImageRefs.current[index] = el}
                                            onMouseMove={(e) => handleBlurbImageMouseMove(e, index)}
                                            onMouseLeave={() => handleBlurbImageMouseLeave(index)}
                                        />
                                        <div className="blurb-image-overlay"></div>
                                    </div>
                                    <div className="blurb-content">
                                        <h3 className="blurb-headline">{blurb.headline}</h3>
                                        <p className="blurb-description">{blurb.description}</p>
                                    </div>
                                </div>
                                
                                {/* Add CTA after the 4th blurb */}
                                {index === 3 && (
                                    <div className="mid-page-cta">
                                        <h3 className="mid-page-cta-text">Ready to experience the power of AI-assisted writing?</h3>
                                        <button className="mid-page-cta-button" onClick={handleLoginClick}>
                                            Start Writing Now
                                            <span className="cta-arrow">→</span>
                                        </button>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    
                    {/* Final CTA at the bottom */}
                    <div className="final-cta">
                        <h3 className="final-cta-text">Alright, you made it to the bottom. Now it’s time for the fun part—dive in and start writing!</h3>
                        <button className="final-cta-button" onClick={handleLoginClick}>
                            Start writing now - for free
                            <span className="cta-arrow">→</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Login Modal */}
            <LoginModal 
                isOpen={isLoginModalOpen} 
                onClose={handleCloseLoginModal} 
                onLoginSuccess={handleLoginSuccess}
            />
        </div>
    );
};

export default LandingPage;